/* eslint-disable operator-linebreak */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// constants
import { BFR } from '../../lib/Constants';

// actions
import { updateGasSavings } from '../../reducers/GasSavings/actions';

// components
import Info from '../../components/Info';
import Btn from '../../components/Btn';

// containers
import Tooltip from '../Tooltip';

// helpers
import { i18n } from '../../lib/Utils';
import {
  formatCurrency,
  setCurrencyPrecision,
} from '../../../node_modules/@web/tesla-lang';

class GasSavings extends Component {
  constructor(props) {
    super(props);

    this.showGasSavings = this.showGasSavings.bind(this);
    this.setDisclaimerValues = this.setDisclaimerValues.bind(this);

    this.state = {
      showGasSavings: false,
    };
  }

  componentDidMount() {
    const { modelCode, countryCode, dispatchUpdateGasSavings } = this.props;
    if (modelCode !== BFR) {
      dispatchUpdateGasSavings(countryCode, modelCode);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      modelCode,
      countryCode,
      dispatchUpdateGasSavings,
      chargingNeeded,
    } = this.props;
    if (chargingNeeded !== prevProps.chargingNeeded) {
      dispatchUpdateGasSavings(countryCode, modelCode);
    }
  }

  setDisclaimerValues(fuelEfficiency) {
    const { locale, unit } = this.props;
    setCurrencyPrecision(2);

    return {
      COST_PER_KW: formatCurrency(
        fuelEfficiency.disclaimerValues.ppuCostPerUnit,
        locale,
      ),
      FUEL_EFFICIENCY_PER_UNIT:
        fuelEfficiency.disclaimerValues.fuelEfficiencyVal,
      FUEL_EFFICIENCY_UNIT: unit.fuelEfficiency,
      GAS_COST_PER_UNIT: formatCurrency(
        fuelEfficiency.disclaimerValues.gasCostPerUnit,
        locale,
      ),
      GAS_UNIT: unit.volume.default.singular,
    };
  }

  showGasSavings() {
    const { showGasSavings } = this.state;
    this.setState({
      showGasSavings: !showGasSavings,
    });
  }

  renderGasSavingsLabel() {
    const {
      primaryGasSavingsLabel,
      secondaryGasSavingsLabel,
      perMonthLabel,
      gasSavingsDisclaimer,
      routeAssumptionsDisclaimer,
      fuelEfficiency,
      locale,
      chargingNeeded,
    } = this.props;

    const showToolTip =
      gasSavingsDisclaimer != null && gasSavingsDisclaimer !== '';

    return (
      <Info>
        <div className="copy">
          {i18n(primaryGasSavingsLabel, {
            TOTAL: formatCurrency(fuelEfficiency.tripGasSaving, locale, {
              precision: 0,
            }),
          })}
          {!chargingNeeded && <span>{i18n(perMonthLabel)}</span>}
          {showToolTip && (
            <Tooltip>
              {routeAssumptionsDisclaimer}

              {i18n(
                gasSavingsDisclaimer,
                this.setDisclaimerValues(fuelEfficiency),
              )}
            </Tooltip>
          )}
        </div>
        {!chargingNeeded && (
          <p className="caption-copy">{i18n(secondaryGasSavingsLabel)}</p>
        )}
      </Info>
    );
  }

  renderShortTripLabel() {
    const {
      shortTripLabel,
      gasSavingsDisclaimer,
      routeAssumptionsDisclaimer,
      fuelEfficiency,
      CTA,
    } = this.props;
    const showToolTip =
      gasSavingsDisclaimer != null && gasSavingsDisclaimer !== '';
    return (
      <Info>
        <div className="copy">
          {i18n(shortTripLabel)}
          {showToolTip && (
            <Tooltip>
              {routeAssumptionsDisclaimer}

              {i18n(
                gasSavingsDisclaimer,
                this.setDisclaimerValues(fuelEfficiency),
              )}
            </Tooltip>
          )}
        </div>
        <Btn
          styles="route__cta route__cta--info"
          handleClick={this.showGasSavings}
        >
          {CTA.gasSavings}
        </Btn>
      </Info>
    );
  }

  render() {
    const {
      chargingNeeded,
      directionsLoaded,
      efficiencyDataReady,
      noChargers,
      modelCode,
    } = this.props;
    const { showGasSavings } = this.state;

    if (modelCode === BFR) return null;

    if (showGasSavings) return this.renderGasSavingsLabel();

    if (directionsLoaded && efficiencyDataReady && !noChargers) {
      if (chargingNeeded) {
        return this.renderGasSavingsLabel();
      }
      return this.renderShortTripLabel();
    }

    return null;
  }
}

GasSavings.propTypes = {
  gasSavingsDisclaimer: PropTypes.string.isRequired, // Disclaimer tooltip copy
  routeAssumptionsDisclaimer: PropTypes.string.isRequired,
  primaryGasSavingsLabel: PropTypes.string.isRequired, // Gas Saving Label copy
  secondaryGasSavingsLabel: PropTypes.string.isRequired, // 5-day commute disclaimer Label copy
  perMonthLabel: PropTypes.string.isRequired, // Gas Saving time frame Label copy
  CTA: PropTypes.shape({}).isRequired, // All properties for vehicle specific primary and secondary CTA's
  chargingNeeded: PropTypes.bool.isRequired, // When the request trip does not require charging
  shortTripLabel: PropTypes.string.isRequired, // Short trip Gas Saving Label copy
  dispatchUpdateGasSavings: PropTypes.func.isRequired, // Fetches Fuel efficiency data from API
  fuelEfficiency: PropTypes.shape({}).isRequired, // Energy data {metric: , metric: , gasCostPerUnit}
  modelCode: PropTypes.string.isRequired, // Short model code [MS, MX, M3]
  countryCode: PropTypes.string.isRequired, // Short country code [US]
  directionsLoaded: PropTypes.bool.isRequired, // If the directions have successfully returned from API
  efficiencyDataReady: PropTypes.bool.isRequired, // True when efficiency data has returned from service
  locale: PropTypes.string.isRequired, // locale code eg 'en_US'
  noChargers: PropTypes.bool.isRequired, // True when there are not enough superchargers for the trip
  unit: PropTypes.shape({}).isRequired, // Object of metric and imperial units
};

GasSavings.defaultProps = {};

const mapStateToProps = state => ({
  gasSavingsDisclaimer: state.Tooltip.disclaimers.gasSavings,
  routeAssumptionsDisclaimer: state.Tooltip.disclaimers.routeAssumptions,
  primaryGasSavingsLabel: state.RouteSummary.primaryGasSavingsLabel,
  secondaryGasSavingsLabel: state.RouteSummary.secondaryGasSavingsLabel,
  perMonthLabel: state.RouteSummary.perMonthLabel,
  CTA: state.RouteSummary.CTA,
  chargingNeeded: state.Superchargers.chargingNeeded,
  shortTripLabel: state.RouteSummary.shortTripLabel,
  fuelEfficiency: state.GasSavings.fuelEfficiency,
  efficiencyDataReady: state.GasSavings.efficiencyDataReady,
  modelCode: state.Vehicle.modelCode,
  countryCode: state.GoogleMap.region,
  locale: state.App.locale,
  unit: state.App.unit,
  directionsLoaded: state.Directions.directionsLoaded,
  noChargers: state.Superchargers.noChargers,
});

const mapDispatchToProps = dispatch => ({
  dispatchUpdateGasSavings: (countryCode, modelCode) => {
    dispatch(updateGasSavings(countryCode, modelCode.toLowerCase()));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(GasSavings);
